/**
 * useColumnDisplayNames - resuelve los displayName de los campos del cliente
 * desde el column-config admin (Firestore: clients/{id}/meta/columnConfig).
 *
 * Carga una sola vez por clientId y devuelve helpers:
 *   - displayName(field): string -> displayName configurado o el fieldName raw
 *   - sourceField(field): string|null -> sourceField configurado en native (si aplica)
 *
 * Uso típico:
 *   const { displayName, sourceField, loading } = useColumnDisplayNames(clientId);
 *   <Label>{displayName('Marca')}</Label>
 */
import { useEffect, useMemo, useState } from 'react';
import { getColumnConfig, type ColumnConfigDoc } from '../services/columnConfig';

export interface ColumnDisplayNamesResult {
  loading: boolean;
  config: ColumnConfigDoc | null;
  /**
   * Devuelve el displayName configurado para `field` (busca en core y native)
   * o el `field` tal cual si no hay nada configurado.
   */
  displayName: (field: string) => string;
  /**
   * Si existe entry en native con sourceField, devuelve el campo SQL real
   * que mapea esa columna; si no, null.
   */
  sourceField: (field: string) => string | null;
  /**
   * Devuelve el primer campo nativo que matchee uno de los nombres alternativos
   * pasados (case-insensitive). Útil para resolver "udm" vs "UDM" vs "inches".
   */
  resolveAlias: (aliases: string[]) => string | null;
  reload: () => Promise<void>;
}

export function useColumnDisplayNames(clientId: string | null | undefined): ColumnDisplayNamesResult {
  const [config, setConfig] = useState<ColumnConfigDoc | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await getColumnConfig(clientId);
      setConfig(r.columnConfig || null);
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setConfig(null);
    if (clientId) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const helpers = useMemo(() => {
    const core = config?.core || {};
    const native = config?.native || {};
    const lowerToKey = new Map<string, { key: string; in: 'core' | 'native' }>();
    Object.keys(core).forEach(k => lowerToKey.set(k.toLowerCase(), { key: k, in: 'core' }));
    Object.keys(native).forEach(k => lowerToKey.set(k.toLowerCase(), { key: k, in: 'native' }));

    const displayName = (field: string): string => {
      if (!field) return field;
      const c = core[field];
      if (c?.displayName) return c.displayName;
      const n = native[field];
      if (n?.displayName) return n.displayName;
      // Lookup case-insensitive
      const hit = lowerToKey.get(field.toLowerCase());
      if (hit) {
        const entry = hit.in === 'core' ? core[hit.key] : native[hit.key];
        if (entry?.displayName) return entry.displayName;
      }
      return field;
    };

    const sourceField = (field: string): string | null => {
      const n = native[field];
      if (n?.sourceField) return n.sourceField;
      const hit = lowerToKey.get(field.toLowerCase());
      if (hit && hit.in === 'native') {
        const entry = native[hit.key];
        if (entry?.sourceField) return entry.sourceField;
      }
      return null;
    };

    const resolveAlias = (aliases: string[]): string | null => {
      for (const a of aliases) {
        const hit = lowerToKey.get(a.toLowerCase());
        if (hit) return hit.key;
      }
      return null;
    };

    return { displayName, sourceField, resolveAlias };
  }, [config]);

  return {
    loading,
    config,
    displayName: helpers.displayName,
    sourceField: helpers.sourceField,
    resolveAlias: helpers.resolveAlias,
    reload,
  };
}

export default useColumnDisplayNames;
